/* eslint-disable no-nested-ternary */
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { Send as SendIcon, GetApp as DownloadIcon } from '@mui/icons-material';
import axios from 'src/utils/axios';
import { useParams } from 'react-router-dom';
import useIsMountedRef from 'src/hooks/useIsMountedRef';
import OfflineView from 'src/views/pages/OfflineView';
import PdfPageViewer from 'src/components/PdfPageViewer';
import { captureException } from '@sentry/browser';
import { useLogTransmitDownloadMutation } from 'src/store/serverState';
import { logAndDownloadTransmitPdf } from '../utils/downloadHelpers';
import type {
  MessageDetails,
  RecipientRow,
  SelectedTransmitData,
  TransmitMethod,
} from '../types';

type PDFPreviewerProps = {
  open: boolean;
  sending: boolean;
  downloading: boolean;
  setDownloading: (value: boolean) => void;
  selectedData: SelectedTransmitData[];
  recipients: RecipientRow[];
  disabled: boolean;
  setDisabled: (value: boolean) => void;
  setSending: (value: boolean) => void;
  onClose: () => void;
  onSend: (buffer: Blob | string) => Promise<void> | void;
  showHeaders: boolean;
  autoDownload?: boolean;
  transmitMethod: TransmitMethod;
  messageDetails: MessageDetails;
  sender: string;
  senderEmail: string;
  fileName: string;
};

const useStyles = makeStyles(() => ({
  dialogContent: {
    width: '1200px',
    padding: 0,
  },
  fileViewer: {
    '& .pdf-canvas': {
      display: 'flex',
      justifyContent: 'center',
      width: '100% !important',
    },
    '& .photo-viewer-container': {
      width: '100% important',
      height: 'auto important',
    },
  },
}));

function PDFPreviewer({
  open,
  sending,
  downloading,
  setDownloading,
  selectedData,
  recipients,
  disabled,
  setDisabled,
  setSending,
  onClose,
  onSend,
  showHeaders,
  autoDownload = false,
  transmitMethod,
  messageDetails,
  sender,
  senderEmail,
  fileName,
}: PDFPreviewerProps) {
  const orderedItems = useMemo(
    () =>
      selectedData.map((data) => {
        if (data.type === 'ATTACHMENT') {
          return {
            type: data.type,
            attachmentId: data.value.id,
          };
        }

        if (data.type === 'CASE_NOTES') {
          return {
            type: data.type,
            queryId: data.value.queryId,
          };
        }

        return {
          type: data.type,
          formId: data?.value?.formId ?? null,
          queryId: data?.value?.queryId ?? null,
          approvalId: data?.value?.approval?.id ?? null,
          authFormId: data?.value?.authForm?.id ?? null,
          draiId: data?.value?.drai?.id ?? null,
        };
      }),
    [selectedData],
  );

  const [previewUrl, setPreviewUrl] = useState('');
  const [previewBlob, setPreviewBlob] = useState<Blob | string>('');
  const classes = useStyles();
  const { caseId } = useParams();
  const [errorCode, setErrorCode] = useState<Record<string, any>>({});
  const isMountedRef = useIsMountedRef();
  const [logTransmitDownload] = useLogTransmitDownloadMutation();
  const hasRecipients = (recipients || []).some(
    (recipient) => recipient.contactInfo,
  );

  const getPreview = useCallback(() => {
    setDisabled(true);
    setPreviewUrl('');
    setPreviewBlob('');
    setDownloading(true);

    axios
      .post(
        `/public/transmit/preview/${caseId}`,
        {
          orderedItems,
          showHeaders,
        },
        {
          responseType: 'arraybuffer',
          headers: {
            Accept: 'application/pdf',
          },
        },
      )
      .then((response) => {
        if (isMountedRef.current) {
          const blob = new Blob([response.data], { type: 'application/pdf' });
          const url = URL.createObjectURL(blob);
          setPreviewUrl(url);
          setPreviewBlob(blob);
          setDisabled(false);
          setDownloading(false);
        }
      })
      .catch((error) => {
        const text = String.fromCharCode.apply(
          null,
          Array.from(new Uint8Array(error)),
        );
        if (error.response?.status) {
          setErrorCode({
            status: error.response.status,
            linked: text,
            case: caseId,
          });
        } else {
          const errorId = captureException(error);
          setErrorCode({
            status: 1001,
            case: caseId,
            linked: errorId,
          });
        }
        setDownloading(false);
      });
  }, [
    caseId,
    isMountedRef,
    orderedItems,
    setDisabled,
    setDownloading,
    showHeaders,
    fileName,
  ]);

  useEffect(() => {
    getPreview();
  }, [getPreview]);

  useEffect(() => {
    if (autoDownload && previewUrl) {
      void handleButton(true);
    }
  }, [autoDownload, previewUrl]);

  const handleButton = async (download: boolean) => {
    setDisabled(true);
    if (download && !autoDownload) {
      setDownloading(true);
      try {
        await logAndDownloadTransmitPdf({
          previewUrl,
          fileName,
          logDownload:
            typeof previewBlob !== 'string' && caseId
              ? () =>
                  logTransmitDownload({
                    caseId,
                    buffer: previewBlob,
                    details: selectedData,
                    messageDetails: {
                      subject: messageDetails.subject,
                      message: messageDetails.message,
                      cc: messageDetails.cc,
                    },
                    sender,
                    senderEmail,
                  }).unwrap()
              : undefined,
        });
      } catch (error) {
        captureException(error);
      } finally {
        setDisabled(false);
        setDownloading(false);
      }
    } else if (autoDownload) {
      window.open(
        `/pdf-preview?file=${encodeURIComponent(previewUrl)}`,
        '_blank',
      );
      setDisabled(false);
      setDownloading(false);
    } else {
      setSending(true);
      await onSend(previewBlob);
      setSending(false);
      setDisabled(false);
    }
  };

  if (autoDownload) return null;

  return (
    <Dialog
      style={{ display: 'flex', justifyContent: 'center' }}
      open={open}
      onClose={onClose}
      maxWidth="xl"
      scroll="paper"
      fullWidth
    >
      {errorCode?.status ? (
        <OfflineView
          noHome
          errorInformation={errorCode}
        />
      ) : (
        <>
          <DialogTitle>
            <Typography style={{ fontWeight: 'bold' }}>
              Attachment Preview
            </Typography>
          </DialogTitle>
          <DialogContent
            classes={{ root: classes.dialogContent }}
            dividers
          >
            {previewUrl ? (
              <PdfPageViewer
                id="pdf-viewer"
                file={previewBlob || previewUrl}
                height="90vh"
              />
            ) : (
              <Box
                display="flex"
                gap="15px"
              >
                <CircularProgress size={24} />
                <Typography>Please wait... Generating PDF...</Typography>
              </Box>
            )}
          </DialogContent>
          {previewUrl && (
            <DialogActions>
              <Button
                variant="contained"
                color="secondary"
                endIcon={sending ? null : <SendIcon />}
                startIcon={
                  sending ? (
                    <CircularProgress
                      style={{ color: 'white' }}
                      size={24}
                    />
                  ) : null
                }
                disabled={
                  disabled ||
                  downloading ||
                  sending ||
                  selectedData.length === 0 ||
                  !hasRecipients
                }
                onClick={() => void handleButton(false)}
              >
                {sending ? 'Sending' : `Send ${transmitMethod.value}`}
              </Button>
              <Button
                variant="contained"
                color="secondary"
                endIcon={downloading ? null : <DownloadIcon />}
                startIcon={
                  downloading ? (
                    <CircularProgress
                      style={{ color: 'white' }}
                      size={24}
                    />
                  ) : null
                }
                disabled={
                  disabled ||
                  downloading ||
                  selectedData.length === 0 ||
                  sending
                }
                onClick={() => void handleButton(true)}
              >
                {downloading ? 'Downloading' : 'Download'}
              </Button>
            </DialogActions>
          )}
        </>
      )}
    </Dialog>
  );
}

export default PDFPreviewer;
